import { useRef, useState } from "react";
import { isCopiedFromBerangkat } from "../../utils/checklistProblem";
import { createSideUpdater } from "../../utils/createSideUpdater";

const useEditorNote = ({
  permittedToEdit,
  isBerangkat,
  pengecekanBerangkatDetilData,
  setPengecekanBerangkatDetilData,
  setPengecekanKembaliDetilData,
  saveNotePengencekanDetail,
  saveDetilMany
}) => {
  // update the detil rows of one side ("berangkat" or "kembali")
  const updateDetilList = createSideUpdater({
    setBerangkat: setPengecekanBerangkatDetilData,
    setKembali: setPengecekanKembaliDetilData,
  });
  const overlayRef = useRef(null);
  const [popoverPengecekanData, setPopoverPengecekanData] = useState({});
  const [showEditDeskripsiTambahanDialog, setShowEditDeskripsiTambahanDialog] =
    useState(false);
  // the hidden photo uploader (EditorFotoUploader) and the photo it is taking: { type: "berangkat" | "kembali", row }
  const fotoUploaderRef = useRef(null);
  const fotoTargetRef = useRef(null);

  // handle UI event section

  // the element the menu is open on, to tell "press the same place again" (closes) from "press another place"
  const menuTargetRef = useRef(null);

  // Opens the menu on the pressed element. OverlayPanel.toggle() can't be used for a press on another place while the
  // menu is open: it hides the menu and shows it again 200ms later with the "visible" value of the moment of the
  // press, so it never shows again and the menu just closes.
  const handleOnClickDeskripsi = (event, row) => {
    if (!permittedToEdit) return;

    const overlay = overlayRef.current;
    const target = event.currentTarget;
    setPopoverPengecekanData({ ...row });

    if (!overlay.isVisible()) {
      menuTargetRef.current = target;
      overlay.show(event, target);
    } else if (menuTargetRef.current === target) {
      overlay.hide();
    } else {
      // another place: close the open menu, then open it there
      menuTargetRef.current = target;
      overlay.hide();
      setTimeout(() => overlayRef.current.show(event, target), 250);
    }
  };

  const handleOnClickEditNote = (event, row) => {
    overlayRef.current.hide();

    // kembali: a note that is only the copy of the berangkat note (the page copies it when the kembali note is empty)
    // is not a note of the kembali row: the dialog starts empty, like the row shows it
    if (
      !isBerangkat &&
      isCopiedFromBerangkat({
        detil: popoverPengecekanData,
        berangkatDetils: pengecekanBerangkatDetilData,
      })
    ) {
      setPopoverPengecekanData({ ...popoverPengecekanData, note: "" });
    }

    setShowEditDeskripsiTambahanDialog(true);
  };

  // the note dialog has a note and a gambar. Saved with savemany (like the photo of the popover), because
  // pengecekanmobildetil/update takes gambar as an uploaded file and not as the url of the file manager.
  const handleOnConfirmEditNote = (row, note) => {
    setShowEditDeskripsiTambahanDialog(false);

    saveDetilMany({
      type: row.tipe,
      labelGrup: row.label_grup,
      pengecekanDetilData: [
        {
          ...row,
          gambar: row.gambar ? row.gambar.split("?serial=")[0] : row.gambar,
        },
      ],
    });
  };

  // press "Take Photo" of the hidden uploader for `row` of side `type` ("berangkat" | "kembali")
  const takeFoto = (type, row) => {
    fotoTargetRef.current = { type, row };
    const takePhoto = Array.from(
      fotoUploaderRef.current.querySelectorAll("button")
    ).find((button) => /Take Photo/i.test(button.textContent));
    if (takePhoto) takePhoto.click();
  };

  // "Berangkat" / "Pulang" of the popover: close the popover and take the photo
  const handleOnClickFoto = (event, type) => {
    overlayRef.current.hide();
    takeFoto(type, popoverPengecekanData);
  };

  // "Foto wajib diisi" of a row: takes the photo right away for the side of this page, without the popover
  // (the same as "Berangkat" on the berangkat page / "Pulang" on the kembali page of the popover)
  const handleOnClickFotoRow = (row) => {
    if (permittedToEdit) {
      takeFoto(isBerangkat ? "berangkat" : "kembali", row);
    }
  };

  // a photo was taken / cleared: it is put on the row in the page and saved with the "Save" button of the card
  // (_dirty: changed on this page, not saved yet)
  const handleOnChangeFotoDetil = ({ type, detil, gambar }) => {
    updateDetilList(type, (list) =>
      list.map((el) =>
        el.id === detil.id ? { ...el, gambar, _dirty: true } : el
      )
    );
  };

  return {
    overlayRef,
    popoverPengecekanData,
    setPopoverPengecekanData,
    showEditDeskripsiTambahanDialog,
    setShowEditDeskripsiTambahanDialog,
    handleOnClickDeskripsi,
    handleOnClickEditNote,
    handleOnConfirmEditNote,
    fotoUploaderRef,
    fotoTargetRef,
    handleOnClickFoto,
    handleOnClickFotoRow,
    handleOnChangeFotoDetil
  };
};

export default useEditorNote;
